import { HttpParams } from '@angular/common/http';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Validators } from '@angular/forms';
import { SelectListItem } from '@app/shared/models';
import { ConstrainedField } from '@app/shared/models/constrained-field';
import { ServiceBase, SnackbarFactory } from '@app/shared/services';
import { catchError, debounceTime, filter, map, Observable, of, startWith, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-selectlist-search-cell',
  templateUrl: './selectlist-search-cell.component.html',
  styleUrls: ['./selectlist-search-cell.component.scss'],
})
export class SelectlistSearchCellComponent implements OnInit, OnChanges {
  @Input() public field!: ConstrainedField;
  @Input() public params!: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  public $selectItems: Observable<SelectListItem[]>;
  private storedSelectItems: SelectListItem[] = [];
  
  private service = inject(ServiceBase);
  private snackBar = inject(SnackbarFactory);

  public ngOnInit(): void {
    this.setSearches();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.params && !changes.params.firstChange) {
      this.setSearches();
    }
  }

  public setSearches() {
    if (this.field.control.enabled) {
      this.field.control.disable({ emitEvent: false });
    }

    if (this.field.controlType === "ddlSearch") {
      this.handleDDlSearch();
    }
    else {
      this.handleSearch();
    }
  }

  //DDL Search related

  private handleDDlSearch() {
    let isOriginallyEditable = this.field.editable;
    if (isOriginallyEditable) {
      this.field.control.disable({ emitEvent: false });
    }

    this.service.getRefSelectList(`selectlist/${this.field.key}`, this.params)
      .subscribe({
        next: ({ selectItems, defaultItem }) => {
          this.storedSelectItems = selectItems;
          
          if (this.field.control.value !== null) {
            let origItem = null;
            if (typeof this.field.control.value === "string" || typeof this.field.control.value === "number") {
              origItem = selectItems.find(item => item.id == this.field.control.value);
            }

            if (typeof this.field.control.value === "object" && this.field.control.value.id){
              origItem = selectItems.find(item => item.id == this.field.control.value.id);
            }

            if (origItem) {
              this.field.control.setValue(origItem);
            }
            else {
              this.field.control.setValue(defaultItem ? defaultItem.id : null);
            }
          }
          else {
            this.field.control.setValue(defaultItem ? defaultItem.id : null);
          }

          this.$selectItems = this.field.control.valueChanges
            .pipe(
              startWith(''),
              map((value: string | SelectListItem) => {
                let filterValue = '';
                if (typeof (value) == "string") {
                  filterValue = value?.toLowerCase() ?? '';
                }
                else {
                  filterValue = value?.text?.toLowerCase() ?? '';
                }
                return this.storedSelectItems.filter((listItem) => listItem.text?.toLowerCase().includes(filterValue));
              })
            );

          if (isOriginallyEditable) {
            this.field.control.enable({ emitEvent: false });
          }
        },
        error: err => {
          this.snackBar.showButtonSnackbar(err);
          if (isOriginallyEditable) {
            this.field.control.enable({ emitEvent: false });
          }
        }
      });
  }

  //Search related

  private handleSearch() {
    const originalValue = this.field.control.value;

    if (originalValue != null) {
      let isOriginallyEditable = this.field.editable;
      if (isOriginallyEditable) {
        this.field.control.disable({ emitEvent: false });
      }

      this.getSearchSelectlist(null, originalValue)
        .pipe(
          map(result => {
            return result.selectItems.find(val => { return val.id == originalValue });
          }),
        ).subscribe({
          next: select => {
            this.field.control.setValue(select ?? null);
          },
          complete: () => {
            if (isOriginallyEditable) {
              this.field.control.enable({ emitEvent: false });
            }
            this.setSearchList(true);
          },
          error: (err) => { this.snackBar.showButtonSnackbar(err) }
        });
    }
    else {
      if (this.field.editable) this.field.control.enable({ emitEvent: false });
      this.setSearchList();
    }
  }

  public setSearchList(wasInitialValue = false) {
    this.$selectItems = this.field.control.valueChanges
      .pipe(
        tap(val => {
          if (this.field.validators.includes(Validators.required)) {
            if (!val?.id) {
              this.field.control.setErrors({ 'required': true }, { emitEvent: false })
            }
            else {
              this.field.control.setErrors(null, { emitEvent: false })
            }
          }
        }),
        startWith(this.field.control.value as string),
        debounceTime(350),
        filter((search: string) => search?.length >= 3),
        switchMap((search: string) => {
          return this.getSearchSelectlist(search)
            .pipe(
              map(result => result.selectItems),
              catchError((_err, _caught) => of([]))
            )
        }),
        startWith(wasInitialValue ? [this.field.control.value] : []),
      );
  }

  private getSearchSelectlist(search?, id?) {
    let params = { ...this.params }
    if (search) {
      params['search'] = search;
    }
    if (id) {
      params['id'] = id;
    }

    return this.service.getRefSelectList(`selectlist/search/${this.field.key}`, params);
  }


  // Both related

  public clearInput() {
    this.field.control.setValue(null);
    if (this.field.controlType == "search") {
      this.setSearchList();
    }
  }

  public displayFn(item: SelectListItem): string {
    return item ? item.text : '';
  }

  public canShowClearBtn(): boolean {
    if (this.field.editable == false) {
      return false;
    }
    return this.field.control.value != null && (typeof (this.field.control.value) !== "string")
  }

  public toDbModel(value: SelectListItem | null): string | number {
    return value?.id ?? null;
  }
}
